// THE CELLS — the auth stage's living field, drawn on ONE canvas.
//
// netti turns slowly, and as it turns it draws cells up from beneath it. They
// are born under its lower rim as small seeds of white light in the brand cell
// shape, get carried clockwise around its flank by the turn (potential flow past
// a circle, plus a current bound to it), and as they rise clear of it they
// CONVERT: each seed blooms into a small orb of netti's own light (a white cell
// heart inside a soft halo of its cyan · blue · violet), a baby netti suggested
// rather than drawn. The orbs keep rising, loosening and softening, and
// dissolve into the top edge.
//
// Few of them, and gathered near netti: the field is a plume that leaves the
// creature, not a snowfall across the room. The form's column stays quiet.
//
// Nothing turns (law_mark_stance): cells and orbs only drift and grow.
//
// Why a canvas: DOM nodes clipped to an SVG path repaint on the main thread
// every frame. Here each cell is one or two drawImage calls of sprites made
// once, so a frame is a clear + ~2N blits.

import { CELL } from "../../lib/brand/cell";

type Cell = {
  x: number;
  y: number;
  w: number; // seed width, CSS px
  u: number; // rise speed, px/s
  amp: number; // sideways sway
  f: number;
  ph: number;
  a: number; // base alpha
  orb: number; // which orb sprite it becomes
  far: boolean;
  born: number; // seconds since birth
  life: number; // seconds until it has fully converted
  show: number; // the field's clock time this cell first appears at (stagger)
};

export type CellField = {
  frame: (t: number, dt: number) => void;
  resize: (w: number, h: number) => void;
  /** netti's heart in canvas CSS px and the radius of its circumference */
  setHeart: (x: number, y: number, r: number) => void;
  /** cells quieten left of this x (the form's column) */
  setQuiet: (x: number) => void;
  /** cells quieten below this y (a form under netti, on a phone); 0 = none */
  setQuietBelow: (y: number) => void;
  dispose: () => void;
};

/** netti's light at the door — the same three colours AuthStage paints it in */
/** the orbs' own light: whites, a breath warm and a breath cool — never a hue */
const LIGHT: [number, number, number][] = [
  [255, 251, 242],
  [244, 249, 255],
  [252, 250, 255],
];

export const STAGE_LIGHT: [number, number, number][] = [
  [55, 200, 230], // cyan
  [78, 120, 240], // blue
  [124, 67, 229], // violet
];

const rand = (a: number, b: number) => a + Math.random() * (b - a);
const smooth = (e0: number, e1: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
};

const SPRITE_W = 96; // the cell's width inside a sprite
const PAD = 30; // glow room

function cellPath(g: CanvasRenderingContext2D, cx: number, cy: number, w: number) {
  const s = w / CELL.width;
  g.save();
  g.translate(cx - w / 2, cy - (w / CELL.aspect) / 2);
  g.scale(s, s);
  g.fill(new Path2D(CELL.path));
  g.restore();
}

/** blur a sprite cheaply and portably — down, then back up smooth */
function soften(c: HTMLCanvasElement, by: number): HTMLCanvasElement {
  const small = document.createElement("canvas");
  small.width = Math.max(1, Math.ceil(c.width / by));
  small.height = Math.max(1, Math.ceil(c.height / by));
  small.getContext("2d")!.drawImage(c, 0, 0, small.width, small.height);
  const out = document.createElement("canvas");
  out.width = c.width;
  out.height = c.height;
  const o = out.getContext("2d")!;
  o.imageSmoothingQuality = "high";
  o.drawImage(small, 0, 0, out.width, out.height);
  return out;
}

/** a seed: one flat glowing cell — white light on a deep ground, or netti's
 *  own colour (`tint`) on a pale one, where white would vanish */
function makeSeed(tint?: [number, number, number]): HTMLCanvasElement {
  const ch = SPRITE_W / CELL.aspect;
  const c = document.createElement("canvas");
  c.width = Math.ceil(SPRITE_W + PAD * 2);
  c.height = Math.ceil(ch + PAD * 2);
  const g = c.getContext("2d")!;
  const [tr, tg, tb] = tint ?? [255, 255, 255];
  g.fillStyle = `rgb(${tr},${tg},${tb})`;
  g.shadowColor = `rgba(${tr},${tg},${tb},${tint ? 0.55 : 0.9})`;
  g.shadowBlur = 16;
  cellPath(g, c.width / 2, c.height / 2, SPRITE_W);
  g.shadowBlur = 0;
  g.shadowColor = "transparent";
  cellPath(g, c.width / 2, c.height / 2, SPRITE_W);
  return c;
}

/** an orb: a speck of light — never a bubble. Brightest at its middle and
 *  falling off softly through the cell's shape into a quiet halo of netti's
 *  colour, with a small white heart: a baby netti suggested, never drawn.
 *  `lead` picks the colour, so neighbouring orbs differ. */
function makeOrb(lead: number, palette: [number, number, number][] = STAGE_LIGHT): HTMLCanvasElement {
  const S = 192;
  const c = document.createElement("canvas");
  c.width = S;
  c.height = S;
  const g = c.getContext("2d")!;
  const cx = S / 2, cy = S / 2;
  const [r0, g0, b0] = palette[lead % palette.length];
  // the halo: the colour it carries, breathed onto the air
  const halo = g.createRadialGradient(cx, cy, 0, cx, cy, S / 2);
  halo.addColorStop(0, `rgba(${r0},${g0},${b0},0.5)`);
  halo.addColorStop(0.3, `rgba(${r0},${g0},${b0},0.16)`);
  halo.addColorStop(1, `rgba(${r0},${g0},${b0},0)`);
  g.fillStyle = halo;
  g.fillRect(0, 0, S, S);
  // the body: light in the cell's shape, white at the middle, the colour at its edge
  const w = S * 0.36;
  const body = g.createRadialGradient(cx, cy, 0, cx, cy, w * 0.55);
  body.addColorStop(0, "rgba(255,250,235,0.7)");
  body.addColorStop(0.5, `rgba(${Math.round(255 - (255 - r0) * 0.3)},${Math.round(255 - (255 - g0) * 0.3)},${Math.round(255 - (255 - b0) * 0.3)},0.5)`);
  body.addColorStop(1, `rgba(${r0},${g0},${b0},0.12)`);
  g.fillStyle = body;
  cellPath(g, cx, cy, w);
  const soft = soften(c, 4);
  // the heart: small, white — the one crisp thing in it
  const o = soft.getContext("2d")!;
  o.fillStyle = "rgba(255,255,255,0.95)";
  o.shadowColor = "rgba(255,255,255,0.9)";
  o.shadowBlur = 12;
  cellPath(o, cx, cy, S * 0.12);
  return soft;
}

export function createCellField(
  canvas: HTMLCanvasElement,
  opts: {
    count: number;
    maxDpr?: number;
    dir?: 1 | -1;
    /** INK — for a pale ground (the birth's own colour): seeds take `palette[0]`
     *  and orbs the palette, drawn OVER the ground rather than added to it
     *  (added light vanishes on a pale ground). Default: white light, added. */
    ink?: boolean;
    palette?: [number, number, number][];
    /** REVEALED ONE BY ONE: seconds between the first cells appearing (0 =
     *  the field is simply there, as on the door) */
    stagger?: number;
    /** QUIETER LIGHT — the orbs drawn smaller (orbScale) and fainter (orbAlpha),
     *  for a room where words sit over the water (the deck). Default 1 · 1. */
    orbScale?: number;
    orbAlpha?: number;
  },
): CellField {
  // D: which way the water runs — −1 rises past netti (desktop), +1 falls away
  // beneath it (the phone's footer). The turn runs against it (S): clockwise
  // when rising, so the underside carries cells LEFT and up the flank;
  // counter-clockwise when falling, so the underside carries them left → right.
  const D = opts.dir ?? -1;
  const S = -D;
  const ctx = canvas.getContext("2d", { alpha: true })!;
  const dpr = Math.min(window.devicePixelRatio || 1, opts.maxDpr ?? 1.5);
  const ink = !!opts.ink;
  const palette = opts.palette?.length ? opts.palette : STAGE_LIGHT;
  const orbScale = opts.orbScale ?? 1;
  const orbAlpha = opts.orbAlpha ?? 1;
  // UNCOLOURED: every cell and orb is light itself, white with its own glow,
  // on any ground. (The palette is kept for callers; it no longer tints them.)
  void palette;
  const seed = makeSeed();
  const seedFar = soften(seed, 4);
  const orbs = [0, 1, 2].map((i) => makeOrb(i, LIGHT));
  const orbsFar = orbs.map((o) => soften(o, 6));
  let W = 1;
  let H = 1;
  let heart = { x: 0, y: 0, r: 0 };
  let quiet = 0;
  let quietBelow = 0;

  // a wide range: many specks, a body of small cells, a few that catch the eye
  const size = () => {
    const r = Math.random();
    if (r < 0.32) return rand(1.6, 3);
    if (r < 0.66) return rand(3, 5.5);
    if (r < 0.9) return rand(5.5, 9);
    return rand(9, 15);
  };

  /** where a cell is born: rising, under netti's lower rim (screen y is down,
   *  so the lower arc is 0..π); falling, along its left flank, from where the
   *  turn carries it down and under */
  const birthplace = (): [number, number] => {
    if (heart.r > 0) {
      const mid = D < 0 ? Math.PI / 2 : Math.PI * 0.8;
      const spread = D < 0 ? 0.46 : 0.25;
      const th = mid + (Math.random() + Math.random() - 1) * Math.PI * spread;
      const r = heart.r * rand(1.03, 1.28);
      return [heart.x + Math.cos(th) * r, heart.y + Math.sin(th) * r];
    }
    return [rand(W * 0.5, W), H + rand(4, 30)];
  };

  const make = (scatter: boolean): Cell => {
    const w = size();
    const far = Math.random() < 0.3;
    let [x, y] = birthplace();
    let born = 0;
    const life = rand(7, 12);
    if (scatter && heart.r > 0 && D > 0) {
      // the first frame, falling: some still on the flank, most already below
      const k = Math.random();
      born = k * life * 1.6;
      if (k < 0.3) {
        const th = Math.PI * rand(0.5, 1.05);
        const r = heart.r * rand(1.04, 1.3);
        x = heart.x + Math.cos(th) * r;
        y = heart.y + Math.sin(th) * r;
      } else {
        const down = (k - 0.3) / 0.7;
        x = heart.x + (Math.random() - 0.35) * W * 0.9;
        y = heart.y + heart.r * rand(1.02, 1.2) + down * Math.max(40, H - heart.y - heart.r) * rand(0.3, 1);
      }
    } else if (scatter && heart.r > 0) {
      // the first frame: the plume is already there, not filling. Lay the cell
      // somewhere along its path — round the flank or risen above it.
      const k = Math.random();
      born = k * life * 1.6;
      if (k < 0.45) {
        const th = Math.PI * (0.25 + k * 2.2); // round the left flank, bottom → top
        const r = heart.r * rand(1.04, 1.4);
        x = heart.x + Math.cos(th) * r;
        y = heart.y + Math.sin(th) * r;
      } else {
        const up = (k - 0.45) / 0.55;
        x = heart.x - heart.r * rand(0.4, 1.6) - up * rand(0, heart.r * 0.9);
        y = heart.y - heart.r * 0.9 - up * (heart.y - heart.r * 0.9 + 40) * rand(0.6, 1.1);
      }
    }
    return {
      x,
      y,
      w,
      u: (22 + w * 2.4 + rand(0, 10)) * (far ? 0.75 : 1),
      amp: rand(4, 12),
      f: rand(0.2, 0.5),
      ph: rand(0, Math.PI * 2),
      a: rand(0.65, 1) * (far ? 0.55 : 1),
      orb: Math.floor(Math.random() * 3),
      far,
      born,
      life,
      show: 0,
    };
  };

  let cells: Cell[] = [];

  const resize = (w: number, h: number) => {
    const first = cells.length === 0;
    if (!first) for (const c of cells) { c.x *= w / W; c.y *= h / H; }
    W = Math.max(1, w);
    H = Math.max(1, h);
    canvas.width = Math.round(W * dpr);
    canvas.height = Math.round(H * dpr);
    if (first) {
      cells = Array.from({ length: opts.count }, () => make(true));
      const st = opts.stagger ?? 0;
      if (st > 0) cells.forEach((c, i) => { c.show = i * st; });
    }
  };

  const blit = (sp: HTMLCanvasElement, x: number, y: number, px: number, alpha: number) => {
    if (alpha < 0.01) return;
    const k = (px * dpr) / sp.width;
    ctx.globalAlpha = Math.min(1, alpha);
    ctx.setTransform(k, 0, 0, k, x * dpr, y * dpr);
    ctx.drawImage(sp, -sp.width / 2, -sp.height / 2);
  };

  let clock = 0;
  const frame = (t: number, dt: number) => {
    clock += dt;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const R = heart.r;
    const R2 = R * R;
    for (let i = 0; i < cells.length; i++) {
      let c = cells[i];
      // past netti the plume loosens and slows a touch as it nears its edge
      const yy = Math.min(1, Math.max(0, c.y / H));
      const edge = D < 0 ? 1 - yy : yy;
      let vx = 0;
      let vy = D * c.u * (1 - 0.35 * edge * edge);
      if (R > 0) {
        // uniform rise past netti's circle: v = V(1 + R²/r²) − 2R²(V·n)n / r²
        // plus the turn — a clockwise current bound to it (tangent (−n_y, n_x),
        // y down), strongest at the rim, which is what carries a seed from
        // beneath netti round its flank and up.
        const dx = c.x - heart.x;
        const dy = c.y - heart.y;
        const r2 = Math.max(dx * dx + dy * dy, R2 * 0.9);
        const r = Math.sqrt(r2);
        const nx = dx / r;
        const ny = dy / r;
        const vn = vy * ny;
        const k = R2 / r2;
        vx = -2 * k * vn * nx;
        vy = vy * (1 + k) - 2 * k * vn * ny;
        const turn = 48 * (R / r) * (R / r) * S;
        vx += -ny * turn;
        vy += nx * turn;
        if (r < R * 1.02) { vx += nx * 40; vy += ny * 40; }
        // once clear of netti, the plume leans gently away into the open water
        // (left when rising, right when falling: the way the turn threw it)
        vx += D * 6 * smooth(R * 0.6, R * 1.4, D * (c.y - heart.y));
      }
      c.x += vx * dt;
      c.y += vy * dt;
      c.born += dt;
      if (c.y < -40 || c.x < -60 || c.x > W + 60 || c.y > H + 80) {
        c = cells[i] = make(false);
      }

      // CONVERSION — by time and by distance together: a seed finishes
      // blooming once it has been carried a while AND has travelled clear of
      // netti (risen past its heart · fallen clear of its underside).
      const byTime = smooth(c.life * 0.35, c.life, c.born);
      const along = D * (c.y - heart.y);
      const c0 = D < 0 ? -R * 0.3 : R * 0.95;
      const c1 = D < 0 ? R * 0.8 : R * 0.95 + 0.45 * Math.max(60, H - heart.y - R);
      const byDist = R > 0 ? smooth(c0, c1, along) : 1 - c.y / H;
      const m = Math.min(byTime, byDist);

      const x = c.x + Math.sin(t * c.f + c.ph) * c.amp * (0.3 + 0.7 * m);
      let alpha = c.a * Math.min(1, c.born / 1.2) * smooth(c.show, c.show + 1.1, clock);
      // the far edge: the orbs dissolve into it rather than leaving (the top
      // of the room · above the page's last line)
      alpha *= D < 0 ? smooth(-10, H * 0.2, c.y) : smooth(H * 0.97, H * 0.76, c.y);
      // the form's column stays quiet
      alpha *= 0.12 + 0.88 * smooth(quiet * 0.75, quiet * 1.1, x);
      if (quietBelow > 0) alpha *= smooth(quietBelow + 70, quietBelow, c.y);
      if (R > 0) {
        const d = Math.hypot(x - heart.x, c.y - heart.y);
        alpha *= smooth(R * 0.99, R * 1.08, d);
      }
      if (alpha < 0.01) continue;

      // the seed fades as the orb blooms around it
      const seedPx = c.w * (1 + 0.5 * m);
      blit(c.far ? seedFar : seed, x, c.y, seedPx * ((SPRITE_W + PAD * 2) / SPRITE_W), alpha * (1 - m) * (1 - m));
      if (m > 0.02) {
        // the orb's heart is 0.2 of its sprite: it grows out of the seed's size
        // its heart is 0.13 of the sprite: the orb grows out of the seed's size.
        // Far orbs sit out of focus — larger, softer, fainter (depth of field).
        const orbPx = ((c.w * (1.4 + 1.1 * m)) / 0.12) * (c.far ? 1.6 : 1) * orbScale;
        const glint = 0.85 + 0.15 * Math.sin(t * 0.9 + c.ph);
        // light ADDS: on a lit blue ground an orb drawn over it greys out;
        // added to it, it glows the way light does in the room
        if (!ink) ctx.globalCompositeOperation = "lighter";
        blit(c.far ? orbsFar[c.orb] : orbs[c.orb], x, c.y, orbPx, alpha * m * glint * (c.far ? (ink ? 0.22 : 0.5) : 0.85) * orbAlpha);
        ctx.globalCompositeOperation = "source-over";
        // LIGHT PARTICLES — a few tiny specks circling a near orb, twinkling,
        // as if the light it gives off were catching in the air around it
        if (!c.far && m > 0.3) {
          const sp = seedFar;
          for (let k = 0; k < 3; k++) {
            const ang = t * (0.5 + 0.18 * k) * (k % 2 ? -1 : 1) + c.ph + k * 2.1;
            const rr = orbPx * (0.16 + 0.07 * k);
            const tw = 0.5 + 0.5 * Math.sin(t * (2.2 + k) + c.ph * 3 + k);
            blit(sp, x + Math.cos(ang) * rr, c.y + Math.sin(ang) * rr * 0.8, 3.2 + k, alpha * m * tw * 0.8 * orbAlpha);
          }
        }
      }
    }
    ctx.globalAlpha = 1;
  };

  return {
    frame,
    resize,
    setHeart: (x, y, r) => { heart = { x, y, r }; },
    setQuiet: (x) => { quiet = x; },
    setQuietBelow: (y) => { quietBelow = y; },
    dispose: () => { cells = []; },
  };
}

/** THE FEATHERED CELL — a cell mask whose OUTLINE is soft while what it reveals
 *  stays sharp (the film engine's featherCell, marketing/film/engine/runtime/
 *  cell.js). PROGRESSIVE, like netti's own cell: the outline is nearly crisp at
 *  the top and softens toward the bottom, and the fill itself thins toward the
 *  foot, so the cell reads as in focus above and dissolving below. Built once;
 *  returned with how much bigger the image is than the cell it holds, so CSS
 *  can size it to any box. */
export function featherCellMask(feather = 0.14): { url: string; kx: number; ky: number } {
  const C = 512;
  const top = feather * 0.35; // the crisp end
  const bottom = feather * 1.55; // the dissolving end
  const pad = Math.ceil(C * bottom * 1.6) + 4;
  const ch = C / CELL.aspect;
  const W = C + pad * 2;
  const H = Math.round(ch) + pad * 2;
  // one blurred cell, as an alpha canvas (only the SHADOW lands on it: the cell
  // is drawn a canvas away and its blurred shadow offset back into place —
  // portable where `ctx.filter` is not; shadowBlur ≈ 2σ)
  const blurred = (f: number) => {
    const cv = document.createElement("canvas");
    cv.width = W;
    cv.height = H;
    const g = cv.getContext("2d")!;
    g.shadowColor = "#fff";
    g.shadowBlur = C * f;
    g.shadowOffsetX = W;
    g.fillStyle = "#fff";
    cellPath(g, W / 2 - W, H / 2, C);
    return cv;
  };
  /** keep `src` only where `a(y)` says, top → bottom */
  const ramp = (src: HTMLCanvasElement, stops: [number, number][]) => {
    const g = src.getContext("2d")!;
    // ⚠️ the blurred canvases still carry their offset shadow; composited
    // destination-in, that shadow would erase the whole canvas
    g.shadowColor = "transparent";
    g.shadowBlur = 0;
    g.shadowOffsetX = 0;
    const lg = g.createLinearGradient(0, pad, 0, H - pad);
    for (const [at, a] of stops) lg.addColorStop(at, `rgba(0,0,0,${a})`);
    g.globalCompositeOperation = "destination-in";
    g.fillStyle = lg;
    g.fillRect(0, 0, W, H);
    return src;
  };
  // crisp above, soft below: three blurs, each handing over to the next across
  // a long overlap (their weights always sum to one), so the softening runs
  // continuously down the cell with no seam where two edges meet
  const mid = (top + bottom) / 2;
  const levels: [number, [number, number][]][] = [
    [top, [[0, 1], [0.2, 1], [0.55, 0], [1, 0]]],
    [mid, [[0, 0], [0.2, 0], [0.55, 1], [0.9, 0], [1, 0]]],
    [bottom, [[0, 0], [0.55, 0], [0.9, 1], [1, 1]]],
  ];
  const out = document.createElement("canvas");
  out.width = W;
  out.height = H;
  const o = out.getContext("2d")!;
  o.globalCompositeOperation = "lighter";
  for (const [f, stops] of levels) o.drawImage(ramp(blurred(f), stops), 0, 0);
  o.globalCompositeOperation = "source-over";
  // …and the fill thins toward the foot
  ramp(out, [[0, 1], [0.25, 0.99], [0.45, 0.96], [0.65, 0.89], [0.85, 0.78], [1, 0.7]]);
  return { url: `url(${out.toDataURL("image/png")})`, kx: W / C, ky: H / ch };
}
